/**
 * Aligning two documents LINE BY LINE, so the word diff inside them is
 * readable.
 *
 * Pure — no React, no DOM — so the alignment, which is the part most likely
 * to be subtly wrong, is unit-testable on its own.
 *
 * ── WHY ALIGNMENT COMES FIRST ────────────────────────────────────────────
 *
 * A word diff over the two documents' whole plain text is correct and
 * unreadable: one changed word near the top shifts the alignment and the rest
 * of the contract renders as one enormous insert next to one enormous delete.
 *
 * So lines are paired first, with a longest-common-subsequence over their
 * exact text — the same shape as `diffWords`, one level up. Only lines that
 * PAIR get a word-level diff; an unpaired line is wholly added or wholly
 * removed, which is what it actually is.
 */
import type { DeltaLineInfo } from './deltaOutline';

export type LinePairKind = 'same' | 'changed' | 'added' | 'removed';

export interface LinePair {
  kind: LinePairKind;
  before?: DeltaLineInfo;
  after?: DeltaLineInfo;
}

/**
 * Above this many lines on either side the quadratic table stops being
 * affordable — an (n+1)×(m+1) `Uint32Array` grid at 2,500 is already ~6M
 * cells for a synchronous diff with no worker. Past it, every line is
 * reported as removed-then-added rather than freezing the tab. The same
 * bail-out `diffWords` makes at its own threshold.
 */
export const MAX_DIFF_LINES = 2500;

/**
 * Are these two lines the same line reworded, or two unrelated lines?
 *
 * Measured as the share of words in common. The threshold is deliberately
 * low: a heavily rewritten clause is still the same clause, and showing it as
 * one changed line beats showing an unrelated deletion beside an unrelated
 * insertion.
 */
export function similarLines(a: string, b: string): boolean {
  if (a === '' || b === '') return false;
  const wa = a.toLowerCase().split(/\s+/).filter(Boolean);
  const wb = new Set(b.toLowerCase().split(/\s+/).filter(Boolean));
  if (!wa.length || !wb.size) return false;
  const shared = wa.filter((w) => wb.has(w)).length;
  return shared / Math.max(wa.length, wb.size) >= 0.3;
}

export function pairLines(
  before: readonly DeltaLineInfo[],
  after: readonly DeltaLineInfo[],
): LinePair[] {
  const n = before.length;
  const m = after.length;

  if (n > MAX_DIFF_LINES || m > MAX_DIFF_LINES) {
    return [
      ...before.map((b) => ({ kind: 'removed' as const, before: b })),
      ...after.map((a) => ({ kind: 'added' as const, after: a })),
    ];
  }

  const lcs: Uint32Array[] = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      lcs[i][j] = before[i].text === after[j].text
        ? lcs[i + 1][j + 1] + 1
        : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const raw: LinePair[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (before[i].text === after[j].text) {
      raw.push({ kind: 'same', before: before[i], after: after[j] });
      i += 1; j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      // The tie goes to `removed`, so a straight rewrite reads as
      // removed-then-added adjacently — which is what lets the merge below
      // recognise it as one changed line.
      raw.push({ kind: 'removed', before: before[i] });
      i += 1;
    } else {
      raw.push({ kind: 'added', after: after[j] });
      j += 1;
    }
  }
  while (i < n) { raw.push({ kind: 'removed', before: before[i] }); i += 1; }
  while (j < m) { raw.push({ kind: 'added', after: after[j] }); j += 1; }

  /**
   * Collapse an adjacent removed/added pair into ONE changed line when the
   * two are recognisably the same line reworded. Without this, editing three
   * words in a clause renders the whole clause twice — once struck through,
   * once green — and a reviewer has to diff it again by eye, which is the
   * job this screen exists to do for them.
   */
  const merged: LinePair[] = [];
  for (let k = 0; k < raw.length; k += 1) {
    const cur = raw[k];
    const nxt = raw[k + 1];
    if (cur.kind === 'removed' && nxt?.kind === 'added'
        && similarLines(cur.before!.text, nxt.after!.text)) {
      merged.push({ kind: 'changed', before: cur.before, after: nxt.after });
      k += 1;
      continue;
    }
    merged.push(cur);
  }
  return merged;
}
